import * as React from 'react';

import {
  emitPatientSurfaceEvent,
  resolvePatientSurfaceFlagVariant,
} from '@/features/patient-profile/analytics';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@heidi/design-system/hover-card';
import { LinkedPatientPopover, type LinkedPatientPopoverProps } from '@heidi/patients';
import type { PatientRecapPopoverOpenedEventProps } from '@heidi/patients/contracts';
import { patientRecapPopoverOpened } from '@heidi/patients/contributions';
import { useCurrentSessionId } from '@heidi/runtime/shell';

interface LinkedPatientHoverCardProps {
  /** Identity + recap + quick-action wiring for the popover body. */
  popover: LinkedPatientPopoverProps;
  /**
   * `patient_recap_popover_opened` surface attribution. Defaults to
   * `'in_session_chip'` (the V1 inline chip); the V2 header trigger
   * passes `'in_session_header'` so the two layouts stay separable in
   * the warehouse. The Iglu schema types `surface` as a free string, so
   * new values validate at ingestion without a schema change — only the
   * schema's known-values description in the snowplow repo needs a
   * (non-blocking) follow-up.
   */
  surface?: PatientRecapPopoverOpenedEventProps['surface'];
  /**
   * Suppress the card while the surface it summarises is already on screen —
   * the V2 header trigger passes this once the patient profile is the open
   * canvas, where the card would replay the recap the profile itself shows
   * and offer a "View profile" action that goes nowhere.
   *
   * Suppressing here rather than dropping the wrapper at the call site keeps
   * the trigger mounted: this flips on the same click that opens the profile,
   * and remounting the trigger mid-interaction drops keyboard focus to
   * `<body>`.
   */
  disabled?: boolean;
  /** The hover trigger — rendered as-is via `asChild`. */
  children: React.ReactNode;
}

/**
 * Hover card shared by every in-session linked-patient affordance — the
 * V1 inline chip (`LinkedPatientChip`) and the V2 header trigger
 * (`V2PatientLinkTrigger`). Hovering the wrapped trigger reveals the
 * patient's identity line, last-session recap, and quick `View profile`
 * / `Unlink from session` actions, and fires
 * `patient_recap_popover_opened` on open.
 */
export function LinkedPatientHoverCard({
  popover,
  surface = 'in_session_chip',
  disabled = false,
  children,
}: LinkedPatientHoverCardProps) {
  const currentSessionId = useCurrentSessionId();
  const [open, setOpen] = React.useState(false);
  const [prevDisabled, setPrevDisabled] = React.useState(disabled);

  // The card has to be *told* it closed, because it can't observe it: "View
  // profile" lives inside the card, so suppression lands with the pointer over
  // the portaled content, which then unmounts under the cursor without ever
  // delivering a pointer-leave. Adjusting during render rather than in an
  // effect settles `open` on the same pass suppression arrives, so there's no
  // committed frame where the two disagree.
  if (prevDisabled !== disabled) {
    setPrevDisabled(disabled);
    if (disabled) setOpen(false);
  }

  return (
    <HoverCard
      open={open}
      openDelay={150}
      closeDelay={100}
      onOpenChange={(nextOpen) => {
        // Radix reports hover intent even while suppressed, so this both keeps
        // the card shut and stops it logging an impression it never drew.
        if (disabled) return;
        setOpen(nextOpen);
        if (!nextOpen) return;
        emitPatientSurfaceEvent({
          event_name: patientRecapPopoverOpened.name,
          props: {
            patient_id: popover.patientProfileId,
            session_id: currentSessionId,
            surface,
            feature_flag_variant: resolvePatientSurfaceFlagVariant(),
          },
        });
      }}
    >
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      {/* `pb-2`: the last action row carries its own 8px of padding, so a full
        `p-3` bottom reads heavier than the top. */}
      <HoverCardContent align="start" sideOffset={8} className="w-96 p-3 pb-2">
        <LinkedPatientPopover {...popover} />
      </HoverCardContent>
    </HoverCard>
  );
}
